import axios from 'axios';
import type { AxiosError, AxiosResponse, GenericAbortSignal } from 'axios';

export enum HTTPMethod {
  CONNECT = 'CONNECT',
  DELETE = 'DELETE',
  GET = 'GET',
  HEAD = 'HEAD',
  OPTIONS = 'OPTIONS',
  PATCH = 'PATCH',
  POST = 'POST',
  PUT = 'PUT',
  TRACE = 'TRACE',
}

const BASE_URL = window.location.origin;

// Mobile browser detection
const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Network error retry logic
const isRetryableError = (error: AxiosError) => {
  return (
    error.code === 'ERR_NETWORK' ||
    error.code === 'ECONNABORTED' ||
    error.code === 'ETIMEDOUT' ||
    (error.response?.status && error.response.status >= 500)
  );
};

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export class Http {
  // Desktop timeout: 10s - Standard for stable connections
  // Mobile timeout: 15s - Extended for potentially unstable mobile networks
  private readonly MOBILE_TIMEOUT_MS = 15000;
  private readonly DESKTOP_TIMEOUT_MS = 10000;

  private readonly maxRetries = isMobile() ? 3 : 1;
  private readonly baseTimeout = isMobile() ? this.MOBILE_TIMEOUT_MS : this.DESKTOP_TIMEOUT_MS;

  private async makeRequest<T>(requestFn: () => Promise<AxiosResponse<T>>, retryCount = 0): Promise<AxiosResponse<T>> {
    try {
      return await requestFn();
    } catch (error) {
      const axiosError = error as AxiosError;

      // Log mobile-specific error details
      if (isMobile()) {
        console.warn('Mobile network error:', {
          code: axiosError.code,
          message: axiosError.message,
          status: axiosError.response?.status,
          retryCount,
          userAgent: navigator.userAgent,
          online: navigator.onLine,
        });
      }

      // Retry logic for mobile browsers
      if (retryCount < this.maxRetries && isRetryableError(axiosError)) {
        const delayMs = Math.pow(2, retryCount) * 1000; // Exponential backoff
        await delay(delayMs);
        return this.makeRequest(requestFn, retryCount + 1);
      }

      throw error;
    }
  }

  private getRequestConfig(tenant?: string, signal?: GenericAbortSignal) {
    const config = {
      timeout: this.baseTimeout,
      headers: {
        Tenant: tenant,
        'tenant-path': window.location.href,
        // Add mobile-specific headers
        ...(isMobile() && {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        }),
      },
      signal,
      withCredentials: true,
    };

    return config;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public get = async <T = any,>(
    url: string,
    tenant?: string,
    signal?: GenericAbortSignal
  ): Promise<AxiosResponse<T>> => {
    return this.makeRequest(() => axios.get<T>(BASE_URL + url, this.getRequestConfig(tenant, signal)));
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public post = async <T = any, B = any>(
    url: string,
    body: B,
    tenant?: string,
    signal?: GenericAbortSignal
  ): Promise<AxiosResponse<T>> => {
    return this.makeRequest(() => axios.post<T>(BASE_URL + url, body, this.getRequestConfig(tenant, signal)));
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public patch = async <T = any, B = any>(
    url: string,
    body: B,
    tenant?: string,
    signal?: GenericAbortSignal
  ): Promise<AxiosResponse<T>> => {
    return this.makeRequest(() => axios.patch<T>(BASE_URL + url, body, this.getRequestConfig(tenant, signal)));
  };
}
